import React from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { Translate } from 'react-localize-redux';
import Button from 'components/atoms/Button';
import { NEW_APP_ID } from '../../constants';
import { getPlatformDisplayName, getSiteIcon, translateText } from '../../utils';
import { getDefaultPlatformName } from '../appCardUtils';
import { AppIcon, PlatformIcon } from '../controls';
import css from '../AppCard.css';
import {
    appsPermissionsPropType,
    configurationPropType,
    moveTargetPropType,
    platformPropType,
    sitePropType,
    userDataPropType,
} from './propTypes';
import SiteRow from './SiteRow';

const APP_DELETE_ICON = '/static/dashboard/img/small-trash.svg';

const AddPlatformBar = ({ onAddPlatform, disabled, platformNames, translate }) => {
    const defaultPlatformName = getDefaultPlatformName(platformNames);

    return (
        <div className={css.addPlatformBar}>
            {platformNames.map(platform => (
                <button
                    key={platform}
                    type="button"
                    disabled={disabled}
                    title={`${translateText(
                        translate,
                        'STATIC.PAGES.APPS_CONFIGURATION.ADD_NEW_PLATFORM_TITLE'
                    )}: ${getPlatformDisplayName(platform)}`}
                    onClick={() => onAddPlatform(platform)}
                >
                    <PlatformIcon site={platform} variant="blue" />
                </button>
            ))}
            <Button
                type="secondary"
                level="level2"
                className={css.addPlatformButton}
                contentClass={css.addPlatformButtonContent}
                disabled={disabled}
                onClick={() => onAddPlatform(defaultPlatformName)}
            >
                + <Translate id="STATIC.PAGES.APPS_CONFIGURATION.ADD_SITE" />
            </Button>
        </div>
    );
};

AddPlatformBar.propTypes = {
    disabled: PropTypes.bool.isRequired,
    onAddPlatform: PropTypes.func.isRequired,
    platformNames: PropTypes.arrayOf(PropTypes.string).isRequired,
    translate: PropTypes.func.isRequired,
};

const AppCard = ({
    app,
    adminMode,
    readOnly,
    isStandardTier,
    configuration,
    appsPermissions,
    userData,
    expandedSites,
    moveTargets,
    platformNames,
    platforms,
    getSiteKey,
    onToggleSite,
    onPatchSite,
    onRefreshStoreDetails,
    onTestFcmIntegration,
    onJumpToDuplicateSite,
    onRestoreDuplicateSite,
    onSaveSite,
    isSiteDirty,
    onCancelSite,
    onDeleteSite,
    onToggleSiteDevelopmentStatus,
    onMoveSite,
    onAppNameChange,
    onSaveAppName,
    onDeleteApp,
    onAddPlatform,
    onTrackAppsEvent,
    translate,
}) => {
    const blocksNewPlatform =
        app.id === NEW_APP_ID || app.sites.some(site => !site.id || site.isSaving || site.isLoadingStoreDetails);

    return (
        <div className={css.appCard}>
            <div className={css.appHeader}>
                <AppIcon src={getSiteIcon(app)} />
                <div className={css.appTitle} id="appNameInput">
                    {!readOnly ? (
                        <>
                            <input
                                className={classNames({ [css.inputError]: !!app.appNameError })}
                                value={app.formName ?? app.name ?? ''}
                                placeholder={translateText(
                                    translate,
                                    'STATIC.PAGES.APPS_CONFIGURATION.PLACEHOLDERS.APP_NAME'
                                )}
                                onChange={event => onAppNameChange(app.id, event.target.value)}
                                onBlur={() => onSaveAppName(app)}
                            />
                            {app.appNameError ? <span className={css.appNameError}>{app.appNameError}</span> : null}
                        </>
                    ) : (
                        app.name
                    )}
                    {adminMode && app.id ? <span className={css.adminId}>({app.id})</span> : null}
                </div>
                {!readOnly && app.sites.length ? (
                    <button
                        type="button"
                        className={css.deleteButton}
                        title={translateText(translate, 'STATIC.PAGES.APPS_CONFIGURATION.DELETE_APP')}
                        aria-label={translateText(translate, 'STATIC.PAGES.APPS_CONFIGURATION.DELETE_APP')}
                        onClick={() => onDeleteApp(app)}
                    >
                        <img src={APP_DELETE_ICON} alt="" />
                    </button>
                ) : null}
            </div>
            {(app.visibleSites || app.sites).map(site => {
                const siteKey = getSiteKey(app.id, site);
                return (
                    <SiteRow
                        key={siteKey}
                        site={site}
                        appId={app.id}
                        appName={app.formName || app.name || ''}
                        adminMode={adminMode}
                        isStandardTier={isStandardTier}
                        readOnly={readOnly}
                        configuration={configuration}
                        appsPermissions={appsPermissions}
                        userData={userData}
                        expanded={!!expandedSites[siteKey]}
                        onToggle={() => onToggleSite(app.id, site)}
                        onPatch={patch => onPatchSite(app.id, site, patch)}
                        onRefreshStoreDetails={storeUrl => onRefreshStoreDetails(app, site, storeUrl)}
                        onTestFcmIntegration={() => onTestFcmIntegration(app.id, site)}
                        onJumpToDuplicateSite={details => onJumpToDuplicateSite(app.id, site, details)}
                        onRestoreDuplicateSite={onRestoreDuplicateSite}
                        onSave={siteToSave => onSaveSite(app, siteToSave)}
                        onCancel={() => onCancelSite(app.id, site)}
                        isSiteDirty={isSiteDirty}
                        platformNames={platformNames}
                        platforms={platforms}
                        onDelete={() => onDeleteSite(app.id, site)}
                        onToggleDevelopmentStatus={nextStatus =>
                            onToggleSiteDevelopmentStatus(app.id, site, nextStatus)
                        }
                        onMoveSite={destinationAppId => onMoveSite(app.id, site, destinationAppId)}
                        moveTargets={moveTargets.filter(
                            targetApp => targetApp.id !== app.id && targetApp.id !== NEW_APP_ID
                        )}
                        translate={translate}
                        onTrackAppsEvent={onTrackAppsEvent}
                    />
                );
            })}
            {!readOnly ? (
                <AddPlatformBar
                    disabled={blocksNewPlatform}
                    onAddPlatform={platform => onAddPlatform(app.id, platform)}
                    platformNames={platformNames}
                    translate={translate}
                />
            ) : null}
        </div>
    );
};

AppCard.propTypes = {
    adminMode: PropTypes.bool.isRequired,
    app: PropTypes.shape({
        appNameError: PropTypes.string,
        formName: PropTypes.string,
        id: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
        name: PropTypes.string,
        sites: PropTypes.arrayOf(sitePropType).isRequired,
        visibleSites: PropTypes.arrayOf(sitePropType),
    }).isRequired,
    appsPermissions: appsPermissionsPropType.isRequired,
    configuration: configurationPropType.isRequired,
    expandedSites: PropTypes.objectOf(PropTypes.bool).isRequired,
    getSiteKey: PropTypes.func.isRequired,
    isSiteDirty: PropTypes.func.isRequired,
    isStandardTier: PropTypes.bool.isRequired,
    moveTargets: PropTypes.arrayOf(moveTargetPropType).isRequired,
    onAddPlatform: PropTypes.func.isRequired,
    onAppNameChange: PropTypes.func.isRequired,
    onCancelSite: PropTypes.func.isRequired,
    onDeleteApp: PropTypes.func.isRequired,
    onDeleteSite: PropTypes.func.isRequired,
    onJumpToDuplicateSite: PropTypes.func.isRequired,
    onMoveSite: PropTypes.func.isRequired,
    onPatchSite: PropTypes.func.isRequired,
    onRefreshStoreDetails: PropTypes.func.isRequired,
    onRestoreDuplicateSite: PropTypes.func.isRequired,
    onSaveAppName: PropTypes.func.isRequired,
    onSaveSite: PropTypes.func.isRequired,
    onTestFcmIntegration: PropTypes.func.isRequired,
    onToggleSite: PropTypes.func.isRequired,
    onToggleSiteDevelopmentStatus: PropTypes.func.isRequired,
    onTrackAppsEvent: PropTypes.func.isRequired,
    platformNames: PropTypes.arrayOf(PropTypes.string).isRequired,
    platforms: PropTypes.arrayOf(platformPropType).isRequired,
    readOnly: PropTypes.bool.isRequired,
    translate: PropTypes.func.isRequired,
    userData: userDataPropType.isRequired,
};

export default AppCard;
